import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { AccessibilityHiddenImg } from '@skillz/accessibility';
import { login } from '../../../store/auth-actions';
import { getMapContent, getUserSchoolProperty, removeSlashes, wait } from '../../../utils/utils';
import Link from '../../ui/link/Link';
import Icon from '../../ui/icons/Icon';
import districtTypes from '../../../enums/districtTypes';
import classes from './Map.module.css';

const Map = ({ showArrow, isExpanded }) => {

    const mapSize = { width: 340, height: 535 };
    const arrowSize = { width: 18, height: 40 };

    const ref = useRef();
    const dispatch = useDispatch();
    const { search, pathname } = useLocation();
    const [scale, setScale] = useState(1);
    const [content, setContent] = useState();
    const [arrowPosition, setArrowPosition] = useState(null);
    const { texts, lang } = useSelector(state => state.translate);
    const { isAuth, user, userDetails } = useSelector(state => state.auth);


    const calculateScale = async () => {
        if (isExpanded === false)
            await wait(301);
        const parentElement = ref?.current?.parentElement;
        if (!parentElement) return;
        const { width, height } = parentElement.getBoundingClientRect();
        let calculatedScale = width / mapSize.width;
        const mapHeightWithScale = mapSize.height * calculatedScale;
        if (mapHeightWithScale > height) {
            calculatedScale = height / mapSize.height;
        }
        setScale(calculatedScale);
    };

    const calculateArrowPosition = () => {
        if (!removeSlashes(pathname)) return;
        const parentElement = ref?.current?.parentElement;
        if (!parentElement) return;
        let currentIslandImgElement = parentElement.querySelector(`#${removeSlashes(pathname)}`);
        if (search) {
            const currentIslandImgElementBySearch = parentElement.querySelector(`[data-search="${search}"]`);
            if (currentIslandImgElementBySearch)
                currentIslandImgElement = currentIslandImgElementBySearch
        }
        if (!currentIslandImgElement) {
            setArrowPosition(null);
            return;
        }
        const styles = window.getComputedStyle ? window.getComputedStyle(currentIslandImgElement) : currentIslandImgElement.currentStyle;
        const top = parseFloat(styles.top) || 0;
        const height = parseFloat(styles.height) || 0;
        const arrowPositionTop = top + (height / 2) - (arrowSize.height / 2);

        const parentRect = parentElement.parentElement.getBoundingClientRect();
        const mapRect = ref?.current?.getBoundingClientRect();
        const arrowPositionLeft = -Math.abs(parentRect.left - mapRect.left) / scale;

        setArrowPosition({ top: arrowPositionTop, left: arrowPositionLeft });
    }

    useEffect(() => {
        calculateScale();
        window.addEventListener("resize", calculateScale);

        return () => {
            window.removeEventListener("resize", calculateScale);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isExpanded]);

    useEffect(() => {
        if (showArrow)
            calculateArrowPosition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scale, showArrow, pathname, search, content]);

    useEffect(() => {

        const updateLinksWithDistrict = (mapContent) => {
            const districtId = getUserSchoolProperty(userDetails, 'districtId');
            return Object.fromEntries(
                Object.entries(mapContent).map(([key, island]) => [
                    key,
                    {
                        ...island,
                        link: {
                            he: island.link?.he?.replace('#DISTRICT#', districtTypes[districtId] || ''),
                            ar: island.link?.ar?.replace('#DISTRICT#', districtTypes[districtId] || ''),
                        },
                    },
                ])
            );
        };

        let mapContent = getMapContent(user, userDetails);
        setContent(updateLinksWithDistrict(mapContent));

    }, [user, userDetails]);


    const generateIslandContent = (key) => {
        const { link, image, textImage, text, stars, searchTerm } = content[key];
        const linkSrc = link[lang] || link?.he;
        const id = linkSrc.includes('?') ? text : removeSlashes(linkSrc);
        const imageSrc = image[lang] || image.he;
        return <>
            <AccessibilityHiddenImg src={imageSrc} id={id} data-search={searchTerm} />
            <div className={classes.Texts}>
                {/* <Icon className={classes.Bubble} name={textImage} ></Icon> */}
                <div className={classes.Text}>{texts[text]}</div>
                {stars ? <div className={classes.Stars}>{stars}+</div> : ''}
            </div>
        </>
    }

    return (
        <div className={classes.Map} style={{ transform: `scale(${scale})` }} ref={ref}>
            {content && Object.keys(content).map(key =>
                <div key={key} className={`${classes[key]} ${classes.Island}`}>
                    {isAuth ?
                        <Link link={content[key].link?.[lang] || content[key].link?.he} objectName={content[key].name} className={classes.Link}>{generateIslandContent(key)}</Link>
                        :
                        <div className={classes.IslandContent} onClick={() => dispatch(login())}>{generateIslandContent(key)}</div>
                    }
                </div>
            )}
            {arrowPosition ? <div className={classes.Triangle} style={{ left: arrowPosition.left, top: arrowPosition.top }}>
            </div> : ''}
        </div>
    );
}

export default Map;
